import { priceForExport } from "./pricing.js";

/**
 * The rules for a launch video's versions, apart from any storage.
 *
 * A customer makes a new version by editing the script or asking in the chat.
 * The last five finished ones are kept; each after the first is charged a price
 * fixed when it is asked for, so the figure shown is the figure taken.
 */

export const KEPT_LAUNCH_VERSIONS = 5;

export type LaunchVersionStatus = "making" | "ready" | "failed" | "removed";

/**
 * What a new version costs: the usual price of what the version it is made
 * from cost to make. A model call's cost is only known afterwards, so the
 * previous version's is the honest figure to quote — and with nothing
 * recorded (a film made before costs were kept) the minimum applies.
 */
export function versionPrice(sourceProviderCostUsd: number | null): number {
  return priceForExport(sourceProviderCostUsd ?? 0);
}

/** Whether the balance covers a version. Free videos never pay for one. */
export function canAffordVersion(balanceUsd: number, priceUsd: number): boolean {
  return balanceUsd + 1e-9 >= priceUsd;
}

/** One past the highest number ever used, so a removed version's number is never seen again. */
export function nextVersionNumber(versions: readonly { number: number }[]): number {
  return versions.reduce((highest, version) => Math.max(highest, version.number), 0) + 1;
}

/** The ready versions beyond the newest `keep`, oldest last-kept first — the ones to delete. */
export function versionsToRemove(
  versions: readonly { id: string; number: number; status: LaunchVersionStatus }[],
  keep = KEPT_LAUNCH_VERSIONS,
): string[] {
  return versions
    .filter((version) => version.status === "ready")
    .sort((a, b) => b.number - a.number)
    .slice(keep)
    .map((version) => version.id);
}

/** A version that was finished at some point: ready now, or ready once and since pruned. */
function everFinished(status: string): boolean {
  return status === "ready" || status === "removed";
}

/**
 * The project's first film: the earliest version that was ever finished.
 *
 * Not simply v1 — a first film that failed can be tried again, and the try
 * that lands is the one charged as the launch video. Null until one lands.
 */
export function firstFilm<T extends { number: number; status: string }>(versions: readonly T[]): T | null {
  return versions.filter((version) => everFinished(version.status)).sort((a, b) => a.number - b.number)[0] ?? null;
}

/**
 * The failed version a first film can be tried again from, or null.
 *
 * Only while no version has ever been finished — after that every new version
 * is a revision of a finished one — and only from the newest, failed: a
 * version still being made is not over yet.
 */
export function firstFilmRetry<T extends { number: number; status: string }>(versions: readonly T[]): T | null {
  if (versions.some((version) => everFinished(version.status))) return null;
  const newest = [...versions].sort((a, b) => b.number - a.number)[0];
  return newest?.status === "failed" ? newest : null;
}
